import type { AdminGrant, ClusterId, ExceptionKey, LeafKey, ModuleKey, PlantId, RoleKey } from '../../../access';

declare const brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [brand]: B };

export type UserId = Brand<string, 'UserId'>;
export type OverrideId = Brand<string, 'OverrideId'>;
export type ExceptionGrantId = Brand<string, 'ExceptionGrantId'>;

export const asUserId = (s: string): UserId => s as UserId;
export const asOverrideId = (s: string): OverrideId => s as OverrideId;
export const asExceptionGrantId = (s: string): ExceptionGrantId => s as ExceptionGrantId;

export interface UnknownValue {
  readonly kind: 'unknown';
  readonly raw: string;
}

export const unknownValue = (raw: string): UnknownValue => ({ kind: 'unknown', raw });

export const isUnknownValue = (value: unknown): value is UnknownValue =>
  typeof value === 'object' && value !== null && (value as { kind?: unknown }).kind === 'unknown';

export type GrantValue = AdminGrant | UnknownValue;
export type ExceptionKeyValue = ExceptionKey | UnknownValue;

export const VERBS = ['create', 'read', 'update', 'delete'] as const;
export type Verb = (typeof VERBS)[number];
export type VerbValue = Verb | UnknownValue;

export type Plane = 'job' | 'admin';

export type OverrideEffect = 'grant' | 'revoke';

export interface ActionTier {
  readonly level: string;
  readonly only: boolean;
}

export interface CatalogueLeaf {
  readonly key: LeafKey;
  readonly module: ModuleKey;
  readonly feature: string;
  readonly featureLabel: string;
  readonly verb: VerbValue | null;
  readonly label: string;
  readonly plane: Plane;
  readonly isAction: boolean;
  readonly fenced: boolean;
  readonly legacyAlias: string | null;
  readonly tier: ActionTier | null;
}

export interface RoleKeySet {
  readonly roleKey: string;
  readonly keys: readonly string[];
}

export interface CatalogueFeature {
  readonly featureKey: string;
  readonly label: string;
  readonly plane: Plane;
  readonly leaves: readonly CatalogueLeaf[];
}

export interface CatalogueModule {
  readonly moduleKey: ModuleKey;
  readonly label: string;
  readonly alwaysOn: boolean;
  readonly features: readonly CatalogueFeature[];
}

export interface ExceptionRule {
  readonly key: ExceptionKey;
  readonly requiresModule: ModuleKey | null;
}

export interface Catalogue {
  readonly modules: readonly CatalogueModule[];
  readonly leavesByKey: ReadonlyMap<LeafKey, CatalogueLeaf>;
  readonly fencedKeys: ReadonlySet<LeafKey>;
  readonly alwaysOnModules: ReadonlySet<ModuleKey>;
  readonly exceptionKeys: readonly ExceptionKey[];
  readonly exceptionRules: readonly ExceptionRule[];
}

export interface StoredOverride {
  readonly overrideId: OverrideId;
  readonly key: LeafKey;
  readonly effect: OverrideEffect;
  readonly reason: string;
}

export interface StoredException {
  readonly grantId: ExceptionGrantId;
  readonly key: ExceptionKeyValue;
  readonly reason: string;
  readonly expiresAt: string | null;
  readonly expired: boolean;
}

export interface PlantAccess {
  readonly plantId: PlantId;
  readonly name: string | null;
  readonly assigned: boolean;
  readonly outOfCluster: boolean;
  readonly licensedModules: readonly ModuleKey[];
  readonly overrides: readonly StoredOverride[];
  readonly exceptions: readonly StoredException[];
  readonly effectiveKeys: ReadonlySet<LeafKey>;
}

export interface Cluster {
  readonly id: ClusterId;
  readonly name: string | null;
  readonly plantCount: number;
}

export interface ClusterValue {
  readonly id: string;
  readonly name: string | null;
}

export interface InviteState {
  readonly sentAt: string | null;
  readonly expiresAt: string | null;
  readonly expired: boolean;
  readonly canResendAt: string | null;
}

export interface InviteSent {
  readonly sentTo: string;
  readonly invite: InviteState;
}

export interface PersonAccess {
  readonly userId: UserId;
  readonly name: string;
  readonly email: string;
  readonly title: string | null;
  readonly company: string | null;
  readonly landing: string | null;
  readonly roleKey: RoleKey | null;
  readonly standardKeys: ReadonlySet<LeafKey>;
  readonly adminGrant: GrantValue | null;
  readonly adminGrantClusterId: ClusterId | null;
  readonly adminGrantClusterName: string | null;
  readonly adminKeys: ReadonlySet<LeafKey>;
  readonly permissionsVersion: number;
  readonly plants: readonly PlantAccess[];
  readonly globalOverrides: readonly StoredOverride[];
}

export interface PersonListItem {
  readonly userId: UserId;
  readonly name: string | null;
  readonly email: string | null;
  readonly title: string | null;
  readonly company: string | null;
  readonly roleKey: RoleKey | null;
  readonly adminGrant: GrantValue | null;
  readonly clusterId: ClusterId | null;
  readonly clusterName: string | null;
  readonly userStatus: string | null;
  readonly plants: readonly { readonly plantId: PlantId; readonly name: string | null }[];
  readonly overrideCount: number;
  readonly exceptionCount: number;
}

export interface PersonListPage {
  readonly items: readonly PersonListItem[];
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

export interface PeopleFilters {
  readonly q?: string;
  readonly plantId?: PlantId;
  /** Only the people with this role. */
  readonly roleKey?: string;
  /** Only the holders of this admin grant. */
  readonly adminGrant?: AdminGrant;
  readonly page?: number;
  readonly limit?: number;
}

export const AUDIT_CHANGE_TYPES = [
  'ROLE_CHANGE',
  'GRANT_CHANGE',
  'OVERRIDE_ADD',
  'OVERRIDE_REMOVE',
  'EXCEPTION_GRANT',
  'EXCEPTION_REVOKE',
  'PLANT_ASSIGN',
  'PLANT_UNASSIGN',
  'LICENCE_CHANGE',
  'USER_DEACTIVATED',
  'USER_REACTIVATED',
  'SESSIONS_REVOKED',
] as const;
export type AuditChangeType = (typeof AUDIT_CHANGE_TYPES)[number];

export type AuditActor = { readonly kind: 'named'; readonly name: string } | { readonly kind: 'unknown' };

/** The stretch of time a read of the trail covered, as the server set it. */
export interface AuditWindow {
  readonly from: string;
  readonly to: string;
  /** The zone the server counts the window's days in; null when it names none. */
  readonly timeZone: string | null;
}

/** What a licence change changed, as the server types it. */
export interface LicenceChange {
  readonly moduleKey: string | null;
  readonly moduleLabel: string | null;
  readonly licensed: boolean | null;
  readonly contractRef: string | null;
}

export interface AuditEntry {
  readonly auditId: string;
  readonly createdAt: string;
  readonly changeType: AuditChangeType | UnknownValue;
  readonly actor: AuditActor;
  readonly plant: { readonly id: PlantId; readonly name: string | null } | null;
  readonly target: { readonly id: UserId; readonly name: string | null } | null;
  /** Typed for a licence change; null for other kinds, which keep before and after. */
  readonly change: LicenceChange | null;
  readonly permissionKey: string | null;
  readonly before: Record<string, unknown> | null;
  readonly after: Record<string, unknown> | null;
  readonly reason: string;
}

export interface AuditPage {
  readonly window: AuditWindow;
  readonly items: readonly AuditEntry[];
  readonly total: number;
  /** Where to continue from; null on the last page. */
  readonly nextCursor: string | null;
  readonly limit: number;
  readonly changeTypes: readonly AuditChangeType[];
}

export interface AuditFilters {
  readonly plantId?: PlantId;
  readonly targetUserId?: UserId;
  readonly changeType?: AuditChangeType;
  /** A window an earlier answer named, so this read covers exactly the same stretch. */
  readonly window?: Pick<AuditWindow, 'from' | 'to'>;
  readonly cursor?: string;
  readonly limit?: number;
}

export interface AuditPlantCount {
  readonly plantId: PlantId;
  readonly plantName: string | null;
  readonly clusterId: string | null;
  readonly clusterName: string | null;
  readonly count: number;
  readonly lastChangeAt: string;
}

/** The trail counted by plant, newest activity first. */
export interface AuditPlantSummary {
  readonly window: AuditWindow;
  readonly total: number;
  readonly plants: readonly AuditPlantCount[];
}

export interface RoleTemplateSummary {
  readonly roleKey: RoleKey;
  readonly name: string;
  readonly description: string;
  readonly version: number;
  readonly isSystem: boolean;
  readonly isLocked: boolean;
  readonly total: number;
  readonly holders: number;
}

export interface AppliedChange {
  readonly permissionKey: LeafKey;
  readonly effect: OverrideEffect | null;
}

export interface CascadedChange extends AppliedChange {
  readonly causedBy: LeafKey | null;
}

export interface Violation {
  readonly code: string;
  readonly message: string | null;
  readonly keys: readonly LeafKey[];
}

export interface WriteResult {
  readonly changed: boolean;
  readonly sessionsRevoked: number;
  readonly forcedReLogin: boolean;
  readonly overridesCleared: number;
  readonly exceptionsRevoked: number;
  readonly message: string | null;
  readonly applied: readonly AppliedChange[];
  readonly cascaded: readonly CascadedChange[];
}

export interface OverridesDryRun {
  readonly applied: readonly AppliedChange[];
  readonly cascaded: readonly CascadedChange[];
  readonly violations: readonly Violation[];
}

export interface PreviewMenuItem {
  readonly name: string;
  readonly link: string | null;
  readonly children: readonly PreviewMenuItem[];
}

export interface AccessPreview {
  readonly plantId: PlantId;
  readonly menu: readonly PreviewMenuItem[];
  readonly landing: string | null;
}

export interface AssignableRole {
  readonly roleKey: RoleKey;
  readonly name: string;
}

export interface Assignable {
  readonly roles: readonly AssignableRole[];
  readonly grants: readonly AdminGrant[];
}

export interface ReasonInput {
  readonly reason: string;
}
export interface ChangeRoleInput extends ReasonInput {
  readonly roleKey: string | null;
}
export interface CreatePersonInput extends ReasonInput {
  readonly name: string;
  readonly email: string;
  /** Ten digits, without the country code. */
  readonly number?: string | null;
  readonly title?: string | null;
  readonly roleKey?: string | null;
  readonly adminGrant?: AdminGrant | null;
  readonly clusterId?: string | null;
  readonly plantIds?: readonly string[];
}
export interface CreatePersonResult {
  readonly userId: UserId;
  /** The first invite, when it went out as the person was added. */
  readonly invite: { readonly sentTo: string; readonly state: InviteState } | null;
  /** Why the first invite did not go out; the person is added all the same. */
  readonly inviteError: string | null;
}
export interface ChangeGrantInput extends ReasonInput {
  readonly grant: string | null;
  readonly clusterId?: string | null;
}
export interface ChangeClusterInput extends ReasonInput {
  readonly clusterId: string | null;
}
export interface PlantAccessInput extends ReasonInput {
  readonly plantId: string;
}
export interface PlantAccessWrite extends PlantAccessInput {
  readonly plantName: string;
}
export interface GrantExceptionInput extends ReasonInput {
  readonly plantId: string;
  readonly exceptionKey: ExceptionKey;
  readonly expiresAt?: string | null;
}
export interface RevokeExceptionInput extends ReasonInput {
  readonly grantId: string;
}

export interface OverrideChange {
  readonly permissionKey: LeafKey;
  readonly effect: OverrideEffect;
}

export interface LeafView {
  readonly key: LeafKey;
  readonly module: ModuleKey;
  readonly standard: boolean;
  readonly override: OverrideEffect | null;
  readonly effective: boolean;
  readonly licensed: boolean;
  readonly fenced: boolean;
}

export interface PendingEntry {
  readonly to: boolean;
  readonly causedBy: LeafKey | null;
}

export interface ChangeRow {
  readonly key: LeafKey;
  readonly label: string;
  readonly to: boolean;
  readonly toStandard: boolean;
  readonly followsDependency: boolean;
  readonly alsoOn: readonly string[];
  readonly alsoOff: readonly string[];
}

export type LeafStatus =
  | 'standard'
  | 'added'
  | 'removed'
  | 'pendingAdd'
  | 'pendingRemove'
  | 'reverting'
  | 'lockedFenced'
  | 'unlicensed';
